import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { MessageCircle, Users, Zap, TrendingUp } from 'lucide-react';

const RequirementsSection = () => {
  const requirements = [
    {
      icon: <MessageCircle className="h-12 w-12 text-blue-600" />,
      title: "B2C & B2B Shops",
      description: "Endkunden-Shops mit Fokus auf Conversion oder B2B-Portale mit individuellen Preisstrukturen, Freigabeprozessen und ERP-Integration - jeder Bereich hat andere Anforderungen.",
      approach: "Wir analysieren deine Zielgruppe und entwickeln gemeinsam die passende Shop-Lösung."
    },
    {
      icon: <Users className="h-12 w-12 text-green-600" />,
      title: "Multi-Channel-Strategien",
      description: "Verkauf über eigenen Shop, Amazon, eBay, Großkunden-Portale oder Franchise-Systeme - alles zentral verwaltet und synchronisiert.",
      approach: "Wir beraten dich bei der Kanalstrategie und entwickeln die technische Umsetzung."
    },
    {
      icon: <Zap className="h-12 w-12 text-purple-600" />,
      title: "Automatisierung & Integration",
      description: "Warenwirtschaft, CRM, Buchhaltung - alle Systeme sollen nahtlos zusammenarbeiten, ohne doppelte Datenpflege oder manuelle Prozesse.",
      approach: "Wir entwickeln Schnittstellen und Automatismen, die deine Arbeitsabläufe optimieren."
    },
    {
      icon: <TrendingUp className="h-12 w-12 text-orange-600" />,
      title: "Skalierung & Wachstum",
      description: "Neue Märkte, mehr Produkte, internationale Expansion - deine E-Commerce-Plattform muss mit deinem Erfolg mitwachsen können.",
      approach: "Wir planen eine zukunftssichere Architektur, die flexibel erweiterbar ist."
    }
  ];

  return (
    <section id="ecommerce-features" className="py-16 md:py-24 bg-gradient-to-br from-blue-50 to-gray-50">
      <div className="container">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            <span className="text-blue-600">Individuelle E-Commerce-Lösungen</span> für dein Geschäft
          </h2>
          <p className="text-lg text-gray-600">
            Jedes Unternehmen ist einzigartig. Wir entwickeln E-Commerce-Plattformen, 
            die perfekt zu deinen Geschäftsprozessen und Zielen passen.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {requirements.map((requirement, index) => (
            <Card key={index} className="border-0 shadow-lg bg-white h-full hover:shadow-xl transition-all duration-300">
              <CardContent className="p-8 h-full flex flex-col">
                <div className="flex items-center mb-6">
                  {requirement.icon}
                  <h3 className="text-xl font-bold ml-4">{requirement.title}</h3>
                </div>
                
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-800 mb-2">Typische Anforderungen:</h4>
                  <p className="text-gray-600 leading-relaxed flex-grow">
                    {requirement.description}
                  </p>
                </div>
                
                <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-400 mt-auto">
                  <h4 className="font-semibold text-blue-800 mb-1">Unser Beratungsansatz:</h4>
                  <p className="text-blue-700 text-sm">
                    {requirement.approach}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Strategic Partnership */}
        <div className="bg-white rounded-xl shadow-xl p-8 max-w-4xl mx-auto text-center">
          <h3 className="text-2xl font-bold mb-4 text-gray-900">
            Strategische Partnerschaft statt bloße Umsetzung
          </h3>
          <p className="text-lg text-gray-600 mb-6">
            Wir setzen nicht blind um, was du dir vorstellst. Stattdessen entwickeln wir gemeinsam 
            eine E-Commerce-Strategie, die zu deinen Geschäftszielen passt und nachhaltigen Erfolg bringt.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="font-bold text-blue-800 mb-2">1. Analyse</h4>
              <p className="text-blue-700 text-sm">Geschäftsprozesse verstehen</p>
            </div>
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="font-bold text-blue-800 mb-2">2. Strategie</h4>
              <p className="text-blue-700 text-sm">Gemeinsame Lösungsentwicklung</p>
            </div>
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="font-bold text-blue-800 mb-2">3. Umsetzung</h4>
              <p className="text-blue-700 text-sm">Technische Realisierung</p>
            </div>
          </div>
          <div className="bg-green-50 rounded-lg p-6">
            <p className="text-green-800 font-semibold">
              💡 Beratung und Entwicklung aus einer Hand - für E-Commerce-Lösungen, die wirklich funktionieren.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RequirementsSection; 